/* KOSH motion + mobile layer (with assets/kosh-motion.css; loaded from snippets/kosh-head.liquid).
   - Soft fade-up for content as it scrolls into view (only content that starts below the fold)
   - Swipe between the two photos on product cards (touch screens)
   - Thin progress bar + count on the product gallery (phones)
   - Smooth open/close for the product info accordions
   - Bag icon pulse after adding to bag; shop links in an empty bag
   - Footer link groups fold away on phones
   No dependencies. Reduced-motion visitors get the layout changes without the movement. */
(() => {
  if (window.__koshMotion) return;
  window.__koshMotion = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const touch = window.matchMedia('(hover: none)').matches;
  const phone = window.matchMedia('(max-width: 749px)');
  const inDesign = !!window.Shopify?.designMode;

  /* ---------- Scroll reveal ---------- */
  const REVEAL = [
    'main .shopify-section .title-wrapper-with-link',
    'main .shopify-section h2.title',
    'main .collection-list-title',
    'main .kosh-section-title',
    'main .image-with-text__text-item',
    'main .kosh-reasons__item',
    'main .newsletter__wrapper > *',
    'main .rich-text__blocks > *',
    'main .main-page-title',
    'main .rte > h2',
    'main .rte > p',
    'main .kosh-desc',
    'main .kosh-info',
    'main .related-products__heading',
    '.footer-block',
  ];
  const STAGGER = ['main .product-grid > .grid__item', 'main .collection-list > .grid__item', 'main .related-products .grid__item'];
  const IMG_REVEAL = ['main .image-with-text__media'];

  const io =
    !reduce && !inDesign && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              e.target.classList.add('is-in');
              io.unobserve(e.target);
            }),
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight * 0.92;
  const mark = (el, cls, delay) => {
    if (!io || el.dataset.kmDone) return;
    el.dataset.kmDone = '1';
    if (!belowFold(el)) return; // already on screen: never hide it
    el.classList.add(cls);
    if (cls === 'km-reveal-img') el.classList.add('km-reveal');
    if (delay) el.style.setProperty('--km-delay', `${delay}ms`);
    io.observe(el);
  };
  const initReveal = (root = document) => {
    if (!io) return;
    root.querySelectorAll(REVEAL.join(',')).forEach((el) => mark(el, 'km-reveal'));
    root.querySelectorAll(IMG_REVEAL.join(',')).forEach((el) => mark(el, 'km-reveal-img'));
    STAGGER.forEach((sel) => {
      const cols = phone.matches ? 2 : 4;
      root.querySelectorAll(sel).forEach((el, i) => mark(el, 'km-reveal', (i % cols) * 90));
    });
  };

  /* ---------- Product cards: swipe to the second photo (touch) ----------
     Dawn lays the card's link over the whole card (a::after), so touches land on the link, not the photo.
     The listeners therefore sit on the card and only react to swipes that start over the photo. */
  const initCards = (root = document) => {
    if (!touch) return;
    root.querySelectorAll('.card__media .media--hover-effect').forEach((media) => {
      if (media.dataset.kmSwipe) return;
      const imgs = media.querySelectorAll(':scope > img');
      if (imgs.length < 2) return;
      const card = media.closest('.card-wrapper') || media.closest('.card') || media;
      media.dataset.kmSwipe = '1';
      media.classList.add('km-swipe');
      card.classList.add('km-swipe-card');
      const dots = document.createElement('span');
      dots.className = 'km-card-dots';
      dots.setAttribute('aria-hidden', 'true');
      dots.innerHTML = '<i class="is-on"></i><i></i>';
      media.appendChild(dots);
      let x0 = 0;
      let y0 = 0;
      let tracking = false;
      let swiped = false;
      const set = (alt) => {
        media.classList.toggle('km-alt', alt);
        dots.children[0].classList.toggle('is-on', !alt);
        dots.children[1].classList.toggle('is-on', alt);
      };
      card.addEventListener(
        'touchstart',
        (e) => {
          const t = e.touches[0];
          const r = media.getBoundingClientRect();
          tracking = t.clientX >= r.left && t.clientX <= r.right && t.clientY >= r.top && t.clientY <= r.bottom;
          x0 = t.clientX;
          y0 = t.clientY;
          swiped = false;
        },
        { passive: true }
      );
      card.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;
          const t = e.changedTouches[0];
          const dx = t.clientX - x0;
          const dy = t.clientY - y0;
          if (Math.abs(dx) > 24 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            swiped = true;
            set(dx < 0 ? true : false);
          }
        },
        { passive: true }
      );
      // A swipe should not open the product; a tap still does
      card.addEventListener(
        'click',
        (e) => {
          if (!swiped) return;
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        },
        true
      );
    });
  };

  /* ---------- Product gallery progress bar (phones) ---------- */
  const initGallery = (root = document) => {
    root.querySelectorAll('slider-component[id^="GalleryViewer-"]').forEach((slider) => {
      if (slider.dataset.kmBar) return;
      const list = slider.querySelector('.product__media-list');
      if (!list) return;
      const slides = () => Array.from(list.children).filter((li) => li.offsetParent !== null || li.getClientRects().length);
      if (slides().length < 2) return;
      slider.dataset.kmBar = '1';
      const bar = document.createElement('div');
      bar.className = 'km-gallery-bar';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="km-gallery-bar__track"><span class="km-gallery-bar__fill"></span></span><span class="km-gallery-bar__count"></span>';
      slider.insertAdjacentElement('afterend', bar);
      const fill = bar.querySelector('.km-gallery-bar__fill');
      const count = bar.querySelector('.km-gallery-bar__count');
      const update = () => {
        const all = slides();
        const n = all.length || 1;
        const w = all[0] ? all[0].getBoundingClientRect().width : list.clientWidth;
        const i = Math.min(n - 1, Math.max(0, Math.round(list.scrollLeft / (w || 1))));
        fill.style.setProperty('--km-w', `${100 / n}%`);
        fill.style.setProperty('--km-x', `${i * 100}%`);
        count.textContent = `${i + 1} / ${n}`;
      };
      let raf = 0;
      list.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(update);
        },
        { passive: true }
      );
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  };

  /* ---------- Accordions: smooth height ---------- */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details.kosh-acc > summary');
    if (!summary || reduce) return;
    const details = summary.parentElement;
    const body = details.querySelector('.kosh-acc__body');
    if (!body || !body.animate) return;
    e.preventDefault();
    if (details.open) {
      const h = body.offsetHeight;
      const anim = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'cubic-bezier(0.2,0.6,0.2,1)' });
      anim.onfinish = () => {
        details.open = false;
      };
    } else {
      // Close siblings in the same group first (one open at a time)
      const group = details.getAttribute('name');
      if (group) document.querySelectorAll(`details.kosh-acc[name="${group}"][open]`).forEach((d) => d !== details && (d.open = false));
      details.open = true;
      const h = body.offsetHeight;
      body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 320, easing: 'cubic-bezier(0.2,0.6,0.2,1)' });
    }
  });

  /* ---------- Bag: pulse on add, shop links when empty ---------- */
  const bump = () => {
    const icon = document.querySelector('#cart-icon-bubble');
    if (!icon || reduce) return;
    icon.classList.remove('km-bump');
    void icon.offsetWidth;
    icon.classList.add('km-bump');
  };
  const hookCart = () => {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, bump);
      return true;
    }
    return false;
  };
  const addEmptyLinks = (root = document) => {
    root.querySelectorAll('.drawer__inner-empty .cart__warnings, .cart__warnings').forEach((box) => {
      if (box.querySelector('.km-empty-links')) return;
      const wrap = document.createElement('div');
      wrap.className = 'km-empty-links';
      wrap.innerHTML = '<a href="/collections/kurta-sets">Kurta sets</a><a href="/collections/co-ord-sets">Co-ord sets</a>';
      const btn = box.querySelector('.button');
      (btn ? btn.parentElement : box).appendChild(wrap);
    });
  };

  /* ---------- Footer groups fold away on phones ---------- */
  const initFooter = () => {
    document.querySelectorAll('.footer-block--menu').forEach((block, i) => {
      if (block.dataset.kmFoot) return;
      const heading = block.querySelector('.footer-block__heading');
      const list = block.querySelector('.footer-block__details-content');
      if (!heading || !list) return;
      block.dataset.kmFoot = '1';
      block.classList.add('km-foot-group');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'km-foot-toggle';
      btn.setAttribute('aria-expanded', 'false');
      const id = list.id || `KmFoot-${i}`;
      list.id = id;
      btn.setAttribute('aria-controls', id);
      heading.parentNode.insertBefore(btn, heading);
      btn.appendChild(heading);
      btn.addEventListener('click', () => {
        const open = !block.classList.contains('is-open');
        block.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  };

  const init = (root) => {
    initReveal(root);
    initCards(root);
    initGallery(root);
    addEmptyLinks(root);
  };

  /* ---------- Home hero: measure the bar + header above it so the photo fills exactly one screen ---------- */
  const sizeHero = () => {
    const hero = document.querySelector('.kosh-hero');
    if (!hero) return;
    const top = Math.max(0, Math.round(hero.getBoundingClientRect().top + window.scrollY));
    document.documentElement.style.setProperty('--km-hero-offset', `${top}px`);
  };

  const start = () => {
    sizeHero();
    init(document);
    initFooter();
    if (!hookCart()) setTimeout(hookCart, 1500);
    // Cart drawer and product sections re-render: pick up new content
    if ('MutationObserver' in window) {
      let t = 0;
      new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => {
          initCards(document);
          initGallery(document);
          addEmptyLinks(document);
        }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
